const trimEnv = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** Web client ID from Google Cloud Console (OAuth 2.0 Client). */
export function getGoogleClientId(): string | null {
  const clientId = trimEnv(import.meta.env.VITE_GOOGLE_CLIENT_ID);
  return clientId || null;
}

/** Current app origin (browser) or configured production URL. */
export function getAppOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  const configured = trimEnv(import.meta.env.VITE_APP_URL);
  if (configured) {
    try {
      return new URL(configured).origin;
    } catch {
      return configured;
    }
  }
  return 'http://localhost:5173';
}

/** Dev-only: log GCP JavaScript origin to register (fixes origin_mismatch). */
export function logGoogleOAuthDevHint(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;
  if (!getGoogleClientId()) {
    console.warn(
      '[Google OAuth] Set VITE_GOOGLE_CLIENT_ID in client/.env (same as api GOOGLE_CLIENT_ID).',
    );
    return;
  }
  console.info(
    `[Google OAuth] Add this JavaScript origin in Google Cloud Console:\n` +
      `  ${getAppOrigin()}\n` +
      `Docs: docs/google-oauth-setup.md | Run: cd api && npm run google:oauth-checklist`,
  );
}

/** Public marketing pages where One Tap may appear for guests. */
export const GOOGLE_ONE_TAP_PATHS = [
  '/',
  '/shop',
  '/about',
  '/contact',
  '/shipping',
  '/size-guide',
  '/seller-program',
  '/creator-program',
] as const;

/** Legacy key — may contain false dismiss from programmatic cancel (pre-v2). */
const LEGACY_ONE_TAP_DISMISS_KEY = 'google_one_tap_dismissed_until';

/** v2 stores explicit user dismiss only (with reason). */
export const GOOGLE_ONE_TAP_DISMISS_KEY = 'google_one_tap_user_dismiss_v2';

/** Cooldown after the user dismisses One Tap. */
export const GOOGLE_ONE_TAP_DISMISS_MS = 24 * 60 * 60 * 1000;

type OneTapDismissRecord = {
  until: number;
  reason: string;
};

let legacyDismissMigrated = false;

/** Drop legacy dismiss flags set by the old cancel() bug. */
export function migrateGoogleOneTapDismissStorage(): void {
  if (legacyDismissMigrated || typeof window === 'undefined') return;
  legacyDismissMigrated = true;
  if (localStorage.getItem(LEGACY_ONE_TAP_DISMISS_KEY)) {
    localStorage.removeItem(LEGACY_ONE_TAP_DISMISS_KEY);
    if (import.meta.env.DEV) {
      console.info('[Google One Tap] cleared legacy dismiss flag');
    }
  }
}

export function isGoogleOneTapPath(pathname: string): boolean {
  if (GOOGLE_ONE_TAP_PATHS.includes(pathname as (typeof GOOGLE_ONE_TAP_PATHS)[number])) {
    return true;
  }
  return pathname.startsWith('/shop/');
}

function readDismissRecord(): OneTapDismissRecord | null {
  migrateGoogleOneTapDismissStorage();
  const raw = localStorage.getItem(GOOGLE_ONE_TAP_DISMISS_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OneTapDismissRecord;
    if (
      typeof parsed.until === 'number' &&
      Number.isFinite(parsed.until) &&
      typeof parsed.reason === 'string'
    ) {
      return parsed;
    }
  } catch {
    // ignore malformed
  }
  localStorage.removeItem(GOOGLE_ONE_TAP_DISMISS_KEY);
  return null;
}

export function isGoogleOneTapDismissed(): boolean {
  const record = readDismissRecord();
  if (!record) return false;
  if (!isExplicitOneTapDismiss(record.reason)) {
    localStorage.removeItem(GOOGLE_ONE_TAP_DISMISS_KEY);
    return false;
  }
  if (Date.now() >= record.until) {
    localStorage.removeItem(GOOGLE_ONE_TAP_DISMISS_KEY);
    return false;
  }
  return true;
}

export function dismissGoogleOneTap(reason: string): void {
  if (!isExplicitOneTapDismiss(reason)) return;
  const record: OneTapDismissRecord = {
    until: Date.now() + GOOGLE_ONE_TAP_DISMISS_MS,
    reason,
  };
  localStorage.setItem(GOOGLE_ONE_TAP_DISMISS_KEY, JSON.stringify(record));
}

/** Reasons where the user explicitly closed One Tap (not programmatic cancel). */
export const EXPLICIT_ONE_TAP_DISMISS_REASONS = [
  'user_cancel',
  'tap_outside',
] as const;

export function isExplicitOneTapDismiss(reason: string): boolean {
  return (EXPLICIT_ONE_TAP_DISMISS_REASONS as readonly string[]).includes(reason);
}

export function getGoogleOneTapDismissedUntil(): number | null {
  const record = readDismissRecord();
  if (!record || !isExplicitOneTapDismiss(record.reason)) return null;
  return record.until;
}
